import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Topbar } from "./Topbar";
import { Footer } from "./Footer";
import { LogoReferences } from "./LogoReferences";
import { Hero } from "./Hero";
import { Section } from "./Section";
import { RevealOnScroll } from "./RevealOnScroll";
import { RelatedServices } from "./RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

const LOGO_MODULES = import.meta.glob(
  "../../assets/logos/delta.{png,svg,webp,jpg}",
  { eager: true, import: "default" },
) as Record<string, string>;
const DELTA_LOGO: string | undefined = Object.values(LOGO_MODULES)[0];

export type DeltaContent = {
  lang: "de" | "en";
  eyebrow: string;
  headline: ReactNode;
  subline: string;
  metaStrip: string[];
  breadcrumbs: { label: string; to?: string }[];

  standpunktKicker: string;
  standpunktHeadline: ReactNode;
  standpunktParagraphs: ReactNode[];

  flowKicker: string;
  flowHeadline: ReactNode;
  flowSourceLabel: string;
  flowEngineLabel: string;
  flowTargetLabel: string;
  flowSources: string[];
  flowEngine: string[];
  flowTargets: string[];

  pillarsKicker: string;
  pillarsHeadline: ReactNode;
  pillars: { num: string; name: string; tagline: string; detail: string }[];

  uspsKicker: string;
  uspsHeadline: ReactNode;
  usps: { num: string; name: string; detail: string }[];

  capsKicker: string;
  capsHeadline: ReactNode;
  caps: { name: string; detail: string }[];

  valueKicker: string;
  valueHeadline: ReactNode;
  values: { strong: string; rest: string }[];

  audienceKicker: string;
  audienceHeadline: ReactNode;
  audience: string[];

  crossKicker: string;
  crossHeadline: ReactNode;
  crossIntro: string;
  crossItems: { label: string; before: string; linkLabel: string; to: string; after: string }[];

  calvariusKicker: string;
  calvariusHeadline: ReactNode;
  calvariusBody: string;

  ctaKicker: string;
  ctaHeadline: ReactNode;
  ctaBody: string;
  ctaPrimary: { label: string; to: string };
  ctaSecondary: { label: string; to: string };
};

function DeltaMark({ size = 28 }: { size?: number }) {
  // Geometric triangle with internal flow lines — Calvarius design language
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden
    >
      <path
        d="M24 6 L42 40 L6 40 Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M14 32 L34 32" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
      <path d="M17 26 L31 26" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
      <path d="M20 20 L28 20" stroke="currentColor" strokeWidth="1.2" opacity="0.35" />
    </svg>
  );
}

function Wordmark({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const cls =
    size === "lg"
      ? "text-5xl md:text-6xl"
      : size === "sm"
      ? "text-2xl"
      : "text-3xl md:text-4xl";
  return (
    <span className="inline-flex items-baseline gap-3 text-ink">
      <span className="text-cyan"><DeltaMark size={size === "lg" ? 44 : size === "sm" ? 22 : 32} /></span>
      <span className={`font-display font-semibold tracking-tight ${cls}`}>Delta</span>
      <span className="font-display italic text-ink-3 text-base md:text-lg">Produktfeed-Management</span>
    </span>
  );
}

function FlowDiagram(p: {
  sourceLabel: string;
  engineLabel: string;
  targetLabel: string;
  sources: string[];
  engine: string[];
  targets: string[];
}) {
  const Col = ({ kicker, items, accent }: { kicker: string; items: string[]; accent: "cyan" | "lime" | "ink" }) => {
    const accentCls =
      accent === "cyan"
        ? "border-cyan/40 bg-cyan/5"
        : accent === "lime"
        ? "border-lime/40 bg-lime/10"
        : "border-rule bg-white";
    return (
      <div className={`flex h-full flex-col rounded-lg border ${accentCls} p-5`}>
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">{kicker}</div>
        <ul className="mt-4 space-y-2 text-sm text-ink-2">
          {items.map((it) => (
            <li key={it} className="flex items-baseline gap-2">
              <span className="text-cyan" aria-hidden>●</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };
  return (
    <div className="relative">
      <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-[1fr_40px_1.1fr_40px_1fr]">
        <Col kicker={p.sourceLabel} items={p.sources} accent="ink" />
        <div className="hidden items-center justify-center md:flex" aria-hidden>
          <svg width="40" height="20" viewBox="0 0 40 20" fill="none">
            <path d="M0 10 L34 10" stroke="oklch(0.78 0.11 220)" strokeWidth="1.5" />
            <path d="M30 4 L38 10 L30 16" stroke="oklch(0.78 0.11 220)" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
        <div className="rounded-lg border border-cyan/40 bg-cyan/5 p-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">{p.engineLabel}</div>
          <div className="mt-4 flex items-center justify-center text-cyan">
            <DeltaMark size={56} />
          </div>
          <ul className="mt-4 space-y-2 text-sm text-ink-2">
            {p.engine.map((it) => (
              <li key={it} className="flex items-baseline gap-2">
                <span className="text-lime" aria-hidden>◆</span>
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="hidden items-center justify-center md:flex" aria-hidden>
          <svg width="40" height="20" viewBox="0 0 40 20" fill="none">
            <path d="M0 10 L34 10" stroke="oklch(0.78 0.11 220)" strokeWidth="1.5" />
            <path d="M30 4 L38 10 L30 16" stroke="oklch(0.78 0.11 220)" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
        <Col kicker={p.targetLabel} items={p.targets} accent="ink" />
      </div>
    </div>
  );
}

export function DeltaPage(p: DeltaContent) {
  const deltaLogoLabel = p.lang === "en" ? "Logo: Delta — Product Feed Management" : "Logo: Delta — Produktfeed-Management";

  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow={p.eyebrow}
          headline={p.headline}
          lead={p.subline}
          metaStrip={p.metaStrip}
          breadcrumbs={p.breadcrumbs}
        />

        {/* Standpunkt */}
        <Section number="02" kicker={p.standpunktKicker} headline={p.standpunktHeadline}>
          <RevealOnScroll>
            <div className="mb-10">
              {DELTA_LOGO ? (
                <img src={DELTA_LOGO} alt={deltaLogoLabel} title={deltaLogoLabel} width={240} height={48} loading="lazy" decoding="async" className="h-12 w-auto" />
              ) : (
                <Wordmark size="lg" />
              )}
            </div>
            <div className="prose max-w-2xl space-y-5 text-[17px] leading-relaxed text-ink-2">
              {p.standpunktParagraphs.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* Flow diagram */}
        <Section variant="tinted" number="03" kicker={p.flowKicker} headline={p.flowHeadline}>
          <RevealOnScroll>
            <FlowDiagram
              sourceLabel={p.flowSourceLabel}
              engineLabel={p.flowEngineLabel}
              targetLabel={p.flowTargetLabel}
              sources={p.flowSources}
              engine={p.flowEngine}
              targets={p.flowTargets}
            />
          </RevealOnScroll>
        </Section>

        {/* Three core areas */}
        <Section number="04" kicker={p.pillarsKicker} headline={p.pillarsHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {p.pillars.map((pi) => (
                <div key={pi.num} className="flex flex-col rounded-lg border border-rule bg-white p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-cyan/10 text-cyan">
                      <DeltaMark size={20} />
                    </span>
                    <span className="font-mono text-xs uppercase tracking-wider text-ink-4">{pi.num}</span>
                  </div>
                  <h3 className="font-display text-xl font-semibold text-ink">{pi.name}</h3>
                  <div className="mt-2 text-sm font-medium text-ink-2">{pi.tagline}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{pi.detail}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* USPs */}
        <Section variant="tinted" number="05" kicker={p.uspsKicker} headline={p.uspsHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4">
              {p.usps.map((u) => (
                <div key={u.num} className="bg-white p-6">
                  <div className="font-mono text-xs uppercase tracking-wider text-ink-4">{u.num}</div>
                  <h3 className="mt-3 font-display text-lg font-semibold text-ink leading-snug">{u.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{u.detail}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* Capabilities */}
        <Section number="06" kicker={p.capsKicker} headline={p.capsHeadline}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {p.caps.map((c, i) => (
                <div key={i} className="bg-white p-5">
                  <div className="font-display text-base font-semibold text-ink">{c.name}</div>
                  <p className="mt-2 text-sm leading-relaxed text-ink-3">{c.detail}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        {/* Value */}
        <Section variant="tinted" number="07" kicker={p.valueKicker} headline={p.valueHeadline}>
          <RevealOnScroll>
            <ul className="space-y-4">
              {p.values.map((it, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-rule bg-white p-5">
                  <span className="text-cyan" aria-hidden>●</span>
                  <div className="text-ink-2 leading-relaxed">
                    <strong className="font-semibold text-ink">{it.strong}</strong> {it.rest && <>— {it.rest}</>}
                  </div>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        {/* Audience */}
        <Section number="08" kicker={p.audienceKicker} headline={p.audienceHeadline}>
          <RevealOnScroll>
            <ul className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {p.audience.map((a, i) => (
                <li key={i} className="flex items-baseline gap-4 bg-white px-5 py-4 text-ink-2">
                  <span className="text-cyan" aria-hidden>●</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </RevealOnScroll>
        </Section>

        <RelatedServices
          locale={p.lang}
          fromId="delta"
          number="09"
          items={relatedBySource["delta"]}
        />

        {/* Calvarius origin */}
        <Section number="10" kicker={p.calvariusKicker} headline={p.calvariusHeadline}>
          <RevealOnScroll>
            <p className="max-w-2xl text-[17px] leading-relaxed text-ink-2">{p.calvariusBody}</p>
          </RevealOnScroll>
        </Section>

        <LogoReferences locale={p.lang} number="11" />

        {/* Single CTA */}
        <Section variant="deep" number="12" kicker={p.ctaKicker}>
          <RevealOnScroll>
            <div className="grid grid-cols-1 items-start gap-10 md:grid-cols-[auto_1fr]">
              <div className="text-cyan">
                {DELTA_LOGO ? (
                  <img src={DELTA_LOGO} alt={deltaLogoLabel} title={deltaLogoLabel} width={320} height={64} loading="lazy" decoding="async" className="h-16 w-auto" />
                ) : (
                  <DeltaMark size={72} />
                )}
              </div>
              <div>
                <h2 className="font-display text-3xl font-semibold leading-tight text-white md:text-4xl">
                  {p.ctaHeadline}
                </h2>
                <p className="mt-4 max-w-2xl leading-relaxed text-white/75">{p.ctaBody}</p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to={p.ctaPrimary.to}
                    className="inline-flex items-center gap-2 rounded-md bg-cyan px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-white transition-colors hover:bg-cyan/90"
                  >
                    {p.ctaPrimary.label} →
                  </Link>
                  <Link
                    to={p.ctaSecondary.to}
                    className="inline-flex items-center gap-2 rounded-md border border-white/30 px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-white transition-colors hover:bg-white/10"
                  >
                    {p.ctaSecondary.label} →
                  </Link>
                </div>
              </div>
            </div>
          </RevealOnScroll>
        </Section>
      </main>
      <Footer />
    </>
  );
}
